import { type ServiceParams, unwrapJson } from "../client";
import type {
  AddMemberRequest,
  CreateWorkspaceRequest,
  ListWorkspacesFilters,
  UpdateMemberRoleRequest,
  UpdateWorkspaceRequest,
  Workspace,
  WorkspaceListResponse,
  WorkspaceMembership,
  WorkspaceMembershipListResponse,
} from "./types";

/**
 * Workspaces service — wraps the ``/v1/workspaces`` admin surface.
 *
 * The set-default endpoint sits at ``/{id}/set-default`` (not
 * ``/default``) — matches the backend route in
 * ``src/workspace/routes.py``.
 */
const Workspaces = ({ url, config }: ServiceParams) => {
  const base = `${url}/v1/workspaces`;

  const buildQuery = (filters?: Record<string, unknown>) => {
    const params = new URLSearchParams();
    if (filters) {
      for (const [key, value] of Object.entries(filters)) {
        if (value !== undefined && value !== null && value !== "") {
          params.set(key, String(value));
        }
      }
    }
    const qs = params.toString();
    return qs ? `?${qs}` : "";
  };

  return {
    /**
     * Convenience helper — returns the items array directly. Used
     * by the workspace selector dropdown + ActiveWorkspaceContext
     * where envelope metadata isn't needed.
     */
    async list(): Promise<Workspace[]> {
      const response = await fetch(base, {
        method: "GET",
        headers: config(),
      });
      const data = await unwrapJson<WorkspaceListResponse>(response);
      return data.items;
    },

    async listPaged(
      filters: ListWorkspacesFilters = {}
    ): Promise<WorkspaceListResponse> {
      const response = await fetch(
        `${base}${buildQuery(filters as Record<string, unknown>)}`,
        { method: "GET", headers: config() }
      );
      return unwrapJson<WorkspaceListResponse>(response);
    },

    async get(id: string): Promise<Workspace> {
      const response = await fetch(`${base}/${id}`, {
        method: "GET",
        headers: config(),
      });
      return unwrapJson<Workspace>(response);
    },

    async create(payload: CreateWorkspaceRequest): Promise<Workspace> {
      const response = await fetch(base, {
        method: "POST",
        headers: config(),
        body: JSON.stringify(payload),
      });
      return unwrapJson<Workspace>(response);
    },

    async update(
      id: string,
      payload: UpdateWorkspaceRequest
    ): Promise<Workspace> {
      const response = await fetch(`${base}/${id}`, {
        method: "PATCH",
        headers: config(),
        body: JSON.stringify(payload),
      });
      return unwrapJson<Workspace>(response);
    },

    /** Soft archive — backend flips ``status`` to ``archived``. */
    async archive(id: string): Promise<void> {
      const response = await fetch(`${base}/${id}`, {
        method: "DELETE",
        headers: config(),
      });
      if (!response.ok) return unwrapJson<void>(response);
    },

    async setDefault(id: string): Promise<Workspace> {
      const response = await fetch(`${base}/${id}/set-default`, {
        method: "POST",
        headers: config(),
      });
      return unwrapJson<Workspace>(response);
    },

    async listMembers(
      id: string,
      filters: { limit?: number; offset?: number } = {}
    ): Promise<WorkspaceMembershipListResponse> {
      const response = await fetch(
        `${base}/${id}/members${buildQuery(filters as Record<string, unknown>)}`,
        { method: "GET", headers: config() }
      );
      return unwrapJson<WorkspaceMembershipListResponse>(response);
    },

    async addMember(
      id: string,
      payload: AddMemberRequest
    ): Promise<WorkspaceMembership> {
      const response = await fetch(`${base}/${id}/members`, {
        method: "POST",
        headers: config(),
        body: JSON.stringify(payload),
      });
      return unwrapJson<WorkspaceMembership>(response);
    },

    async updateMemberRole(
      workspaceId: string,
      userId: string,
      payload: UpdateMemberRoleRequest
    ): Promise<WorkspaceMembership> {
      const response = await fetch(
        `${base}/${workspaceId}/members/${userId}`,
        {
          method: "PATCH",
          headers: config(),
          body: JSON.stringify(payload),
        }
      );
      return unwrapJson<WorkspaceMembership>(response);
    },

    async removeMember(workspaceId: string, userId: string): Promise<void> {
      const response = await fetch(
        `${base}/${workspaceId}/members/${userId}`,
        { method: "DELETE", headers: config() }
      );
      if (!response.ok) return unwrapJson<void>(response);
    },
  };
};

export default Workspaces;
